import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import { useAuthStore } from "@/modules/auth";
import { useFYStore } from "@/modules/financialYear";
import { ErrorCode, type ProblemError } from "./errorCodes";

const baseURL = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

export const api: AxiosInstance = axios.create({ baseURL, withCredentials: true });

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Financial-Year"] = useFYStore.getState().selected;
  return config;
});

interface RefreshResult {
  accessToken: string;
  sessionExpiresAt: string | null;
}

let refreshing: Promise<RefreshResult> | null = null;

async function refreshAccessToken(): Promise<RefreshResult> {
  if (!refreshing) {
    refreshing = axios
      .post<{ accessToken: string; sessionExpiresAt?: string | null }>(
        `${baseURL}/auth/refresh`,
        null,
        {
          withCredentials: true,
          // Marks this as a silent token refresh so the audit middleware skips it
          // (avoids bloating the audit table with a row every ~24h per session).
          // The user-initiated "Extend session" button omits this header.
          headers: { "X-Auto-Refresh": "1" },
        },
      )
      .then((r) => ({
        accessToken: r.data.accessToken,
        sessionExpiresAt: r.data.sessionExpiresAt ?? null,
      }))
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ProblemError>) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const url = error.config?.url ?? "";
    const isRefreshCall = url.includes("/auth/refresh");

    if (
      status === 401 &&
      code === ErrorCode.TOKEN_EXPIRED &&
      error.config &&
      !isRefreshCall
    ) {
      const cfg = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
      if (cfg._retried) {
        useAuthStore.getState().forceLogout();
        throw error;
      }
      cfg._retried = true;
      try {
        const { accessToken: newToken, sessionExpiresAt } = await refreshAccessToken();
        useAuthStore.getState().setAccessToken(newToken, sessionExpiresAt);
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return api(cfg);
      } catch {
        useAuthStore.getState().forceLogout();
        throw error;
      }
    }
    if (code === ErrorCode.TOKEN_REUSED) {
      useAuthStore.getState().forceLogout();
      throw error;
    }
    throw normalizeError(error);
  },
);

export function normalizeError(error: AxiosError<ProblemError>) {
  const data = error.response?.data;
  return {
    code: data?.code ?? "UNKNOWN",
    title: data?.title ?? "Request failed",
    detail: data?.detail ?? error.message,
    fieldErrors: data?.fieldErrors ?? {},
    traceId: data?.traceId,
    status: error.response?.status,
  };
}

function _humanizeFieldKey(key: string): string {
  // snake_case / camelCase → "Title Case"
  const spaced = key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function _flattenFieldErrors(
  fe: string[] | Record<string, unknown> | undefined,
): string[] {
  if (!fe) return [];
  if (Array.isArray(fe)) return fe.filter(Boolean);
  const out: string[] = [];
  const metaKey = /Id$|^id$|DistanceM$|RadiusM$|^code$/;
  for (const [key, val] of Object.entries(fe)) {
    // Validation errors arrive as arrays of strings — always surface them,
    // even when the field key looks like metadata (e.g. departmentId: [
    // "This field is required."]). Only skip the key when the value is a
    // single non-array scalar that looks like an id/coord/code metadata
    // payload tacked onto the error envelope.
    if (!Array.isArray(val) && metaKey.test(key)) continue;

    const msgs: unknown[] = Array.isArray(val) ? val : [val];
    const label = _humanizeFieldKey(key);
    for (const m of msgs) {
      if (m == null || m === "") continue;
      // Only render strings — skip booleans/numbers (they are metadata).
      if (typeof m !== "string") continue;
      out.push(label === "Detail" ? m : `${label}: ${m}`);
    }
  }
  return out;
}

export function toastApiError(
  e: unknown,
  fallback = "Something went wrong",
) {
  const err = e as {
    title?: string;
    detail?: string;
    fieldErrors?: string[] | Record<string, string | string[]>;
    traceId?: string;
    status?: number;
  };
  const lines = _flattenFieldErrors(err?.fieldErrors);
  const headline = err?.detail || err?.title || (err?.status && err.status >= 500
    ? "Server error. Please try again."
    : fallback);

  let body: string | undefined;
  if (lines.length > 0) {
    body = lines.map((l) => `• ${l}`).join("\n");
  }
  if (err?.traceId) {
    const trace = `trace ${err.traceId.slice(0, 8)}`;
    body = body ? `${body}\n\n${trace}` : trace;
  }
  toast.error(headline, body ? { description: body } : undefined);
}
